'use client';

import { Suspense, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import Link from 'next/link';
import { api } from '@/lib/api';
import { useAppStore } from '@/store/app';
import { useRequireAuth } from '@/lib/useRequireAuth';
import EventPing from '@/components/EventPing';
import { formatVolume } from '@/lib/format';
import ResearchTabs from '@/components/ResearchTabs';
import { pickTop, sortValidDesc } from '@/lib/top-pick';

type Tab = 'funding' | 'oi' | 'crowding';

interface FundingRow {
  symbol: string;
  exchange: string;
  fundingRate: number | null;
  rate8h: number | null;
  periodHours: number | null;
  status: 'published' | 'settled' | 'estimated' | null;
  nextFundingTime: number | null;
  fundingPercentile: number | null;
  percentileSamples: number;
  openInterestUsd: number | null;
  longShortRatio: number | null;
  price: number | null;
  priceChangePercent24h: number | null;
  quoteVolume24h: number | null;
}

interface OiRow {
  symbol: string;
  price: number | null;
  priceChangePct: number | null;
  oiChangePct: number | null;
  oiBasis: 'quantity' | 'usd';
  state: 'up_up' | 'down_up' | 'up_down' | 'down_down' | 'flat';
  openInterestUsd: number | null;
  quoteVolume24h: number | null;
}

interface CrowdingRow {
  symbol: string;
  price: number | null;
  fundingRate: number | null;
  fundingPercentile: number | null;
  fundingSamples: number;
  oiPercentile: number | null;
  oiSamples: number;
  longShortRatio: number | null;
  liquidation24hUsd: number | null;
  premiumPct: number | null;
}

function fmtPct(n: number | null, digits = 2): string {
  if (n == null || !isFinite(n)) return '—';
  return `${n >= 0 ? '+' : ''}${n.toFixed(digits)}%`;
}

function fmtPrice(n: number | null): string {
  if (n == null) return '—';
  if (n >= 1000) return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
  if (n >= 1) return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
  return Number(n.toFixed(5)).toLocaleString('en-US', { maximumFractionDigits: 5 });
}

function fmtMoney(n: number | null): string {
  if (n == null || !isFinite(n)) return '—';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(1)}K`;
  return `$${n.toFixed(0)}`;
}

/** 历史分位展示：`100百分位 · 29次结算样本`，样本不足不输出拥挤判断。 */
function fmtPercentile(p: number | null, samples: number, zh: boolean): string {
  if (p == null) return '—';
  const pct = `${p.toFixed(0)}${zh ? '百分位' : 'th pct'}`;
  return samples > 0 ? `${pct} · ${samples}${zh ? '次结算样本' : ' samples'}` : pct;
}

function Badge({ label, tone }: { label: string; tone: 'pos' | 'neg' | 'warn' | 'flat' }) {
  const cls =
    tone === 'pos'
      ? 'bg-gf-positive/10 text-gf-positive'
      : tone === 'neg'
        ? 'bg-gf-negative/10 text-gf-negative'
        : tone === 'warn'
          ? 'bg-gf-warn/10 text-gf-warn'
          : 'bg-gf-surface2 text-gf-muted';
  return <span className={`inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${cls}`}>{label}</span>;
}

function Spark({ points, tone }: { points: Array<{ t: number; v: number }>; tone: 'pos' | 'neg' | 'flat' }) {
  const w = 280;
  const h = 72;
  const path = useMemo(() => {
    const vs = points.map((p) => p.v);
    const min = Math.min(...vs);
    const max = Math.max(...vs);
    const span = max - min || 1;
    const step = points.length > 1 ? w / (points.length - 1) : w;
    return points
      .map((p, i) => {
        const x = i * step;
        const y = h - 4 - ((p.v - min) / span) * (h - 8);
        return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
      })
      .join(' ');
  }, [points]);
  const color = tone === 'pos' ? 'rgb(var(--gf-positive, 22 199 132))' : tone === 'neg' ? 'rgb(var(--gf-negative, 234 57 67))' : 'rgb(var(--gf-muted, 139 139 139))';
  return (
    <svg width="100%" height={h} viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="block">
      <path d={path} fill="none" style={{ stroke: color }} strokeWidth={1.5} />
    </svg>
  );
}

/** 详情抽屉：480px 右侧，展示标的费率/OI 历史曲线与关键指标。 */
function DetailDrawer({ symbol, onClose }: { symbol: string; onClose: () => void }) {
  const { language } = useAppStore();
  const zh = language !== 'en';

  const { data: funding, isLoading: lf } = useQuery({
    queryKey: ['drawer-funding', symbol],
    queryFn: () => api.get<Array<{ exchange: string; data: Array<{ time: number; rate: number }> }>>(`/coins/${symbol}/funding-rate?exchange=Binance&limit=30`),
    staleTime: 30_000,
  });
  const { data: oi, isLoading: lo } = useQuery({
    queryKey: ['drawer-oi', symbol],
    queryFn: () => api.get<Array<{ exchange: string; data: Array<{ time: number; openInterest: number; openInterestValue?: number }> }>>(`/coins/${symbol}/open-interest?exchange=Binance&limit=30`),
    staleTime: 30_000,
  });
  const { data: snap } = useQuery({
    queryKey: ['drawer-snap', symbol],
    queryFn: () => api.get<{ fundingRate: number | null; openInterestUsd: number | null; longShortRatio: number | null; priceChangePercent24h?: number | null } | null>(`/derivatives/${symbol}`),
    staleTime: 30_000,
  });

  const fPoints = funding?.find((r) => r.exchange === 'Binance')?.data ?? [];
  const oiPoints = oi?.find((r) => r.exchange === 'Binance')?.data ?? [];
  const lastRate = fPoints.length ? fPoints[fPoints.length - 1].rate : snap?.fundingRate;
  const lastOi = oiPoints.length ? (oiPoints[oiPoints.length - 1].openInterestValue ?? oiPoints[oiPoints.length - 1].openInterest) : snap?.openInterestUsd ?? null;

  return (
    <div className="fixed inset-0 z-[90] flex justify-end bg-black/40" onClick={onClose}>
      <div
        className="flex h-full w-full max-w-[480px] flex-col overflow-hidden bg-gf-bg shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gf-border px-4 py-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold">{symbol}</span>
            <Link href={`/coin/${symbol}`} className="text-[11px] text-gf-accent hover:underline">
              {zh ? '查看行情 →' : 'View market →'}
            </Link>
          </div>
          <button onClick={onClose} className="rounded p-1 text-gf-muted hover:bg-gf-surface2">
            ✕
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          <div className="grid grid-cols-2 gap-2">
            <div className="card !p-3">
              <div className="text-[10px] text-gf-muted">{zh ? '最新费率' : 'Funding rate'}</div>
              <div className={`mt-0.5 font-mono text-base font-semibold ${(lastRate ?? 0) >= 0 ? 'text-gf-positive' : 'text-gf-negative'}`}>
                {lastRate == null ? '—' : `${(lastRate * 100).toFixed(4)}%`}
              </div>
            </div>
            <div className="card !p-3">
              <div className="text-[10px] text-gf-muted">{zh ? '未平仓' : 'Open interest'}</div>
              <div className="mt-0.5 font-mono text-base font-semibold">{fmtMoney(lastOi)}</div>
            </div>
            <div className="card !p-3">
              <div className="text-[10px] text-gf-muted">{zh ? '多空比' : 'Long/short ratio'}</div>
              <div className="mt-0.5 font-mono text-base font-semibold">{snap?.longShortRatio != null ? snap.longShortRatio.toFixed(2) + 'x' : '—'}</div>
            </div>
            <div className="card !p-3">
              <div className="text-[10px] text-gf-muted">{zh ? '24H 涨跌' : '24h change'}</div>
              <div className="mt-0.5 font-mono text-base font-semibold text-gf-text">
                {snap?.priceChangePercent24h != null ? fmtPct(snap.priceChangePercent24h) : '—'}
              </div>
            </div>
          </div>

          <div className="card !p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-semibold">{zh ? '费率历史（30 点）' : 'Funding history (30 pts)'}</span>
              {lf && <span className="text-[10px] text-gf-muted">{zh ? '载入中…' : 'Loading…'}</span>}
            </div>
            {fPoints.length >= 2 ? (
              <Spark points={fPoints.map((p) => ({ t: p.time, v: p.rate }))} tone={(fPoints[fPoints.length - 1].rate ?? 0) >= 0 ? 'pos' : 'neg'} />
            ) : (
              <div className="py-6 text-center text-xs text-gf-muted">{zh ? '暂无数据' : 'No data'}</div>
            )}
          </div>

          <div className="card !p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="text-xs font-semibold">{zh ? '未平仓历史（30 点）' : 'OI history (30 pts)'}</span>
              {lo && <span className="text-[10px] text-gf-muted">{zh ? '载入中…' : 'Loading…'}</span>}
            </div>
            {oiPoints.length >= 2 ? (
              <Spark points={oiPoints.map((p) => ({ t: p.time, v: p.openInterestValue ?? p.openInterest }))} tone="flat" />
            ) : (
              <div className="py-6 text-center text-xs text-gf-muted">{zh ? '暂无数据' : 'No data'}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function DerivativesBody() {
  const { language } = useAppStore();
  const zh = language !== 'en';
  const [tab, setTab] = useState<Tab>('funding');
  const [selected, setSelected] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<string>('default');

  const funding = useQuery({
    queryKey: ['research-funding', language],
    queryFn: () => api.get<{ asOf: number; rate8hBasis: string; rows: FundingRow[] }>(`/research/derivatives?tab=funding&limit=30&lang=${language}`),
    staleTime: 60_000,
    refetchInterval: 180_000,
  });
  const oiQ = useQuery({
    queryKey: ['research-oi', language],
    queryFn: () => api.get<{ asOf: number; window: string; basis: string; rows: OiRow[] }>(`/research/derivatives?tab=oi&limit=30&lang=${language}`),
    staleTime: 60_000,
    refetchInterval: 180_000,
  });
  const crowding = useQuery({
    queryKey: ['research-crowding', language],
    queryFn: () => api.get<{ asOf: number; basis: string; rows: CrowdingRow[] }>(`/research/derivatives?tab=crowding&limit=15&lang=${language}`),
    staleTime: 60_000,
    refetchInterval: 180_000,
  });

  const tabs: Array<{ id: Tab; label: string }> = [
    { id: 'funding', label: zh ? '费率' : 'Funding' },
    { id: 'oi', label: zh ? 'OI 状态' : 'OI state' },
    { id: 'crowding', label: zh ? '拥挤度' : 'Crowding' },
  ];

  const rows = tab === 'funding' ? funding.data?.rows ?? [] : tab === 'oi' ? oiQ.data?.rows ?? [] : crowding.data?.rows ?? [];
  const basis = tab === 'funding' ? funding.data?.rate8hBasis : tab === 'oi' ? oiQ.data?.basis : crowding.data?.basis;
  const loading = tab === 'funding' ? funding.isLoading : tab === 'oi' ? oiQ.isLoading : crowding.isLoading;

  // 摘要
  const summary = useMemo(() => {
    const noCmp = zh ? '暂无可比较数据' : 'No comparable data';
    const cov = (valid: number, total: number) =>
      valid < total && total > 0 ? ` · ${zh ? '覆盖' : 'Coverage'} ${valid}/${total}` : '';
    if (tab === 'funding') {
      const r = funding.data?.rows ?? [];
      const max8h = pickTop(r, (x) => x.rate8h);
      const maxOi = pickTop(r, (x) => x.openInterestUsd);
      const withPct = r.filter((x) => x.fundingPercentile != null).length;
      return [
        {
          label: (zh ? '最高费率 (8h)' : 'Highest rate (8h)') + cov(max8h.valid, max8h.total),
          value: max8h.top && max8h.top.rate8h != null ? `${max8h.top.symbol} ${(max8h.top.rate8h * 100).toFixed(4)}%` : noCmp,
          tone: ((max8h.top?.rate8h ?? 0) >= 0 ? 'pos' : 'neg') as 'pos' | 'neg',
        },
        {
          label: (zh ? '最大未平仓' : 'Largest OI') + cov(maxOi.valid, maxOi.total),
          value: maxOi.top && maxOi.top.openInterestUsd != null ? `${maxOi.top.symbol} ${fmtMoney(maxOi.top.openInterestUsd)}` : noCmp,
          tone: 'pos' as const,
        },
        { label: zh ? '有分位样本' : 'With percentile', value: `${withPct}`, tone: 'flat' as const },
      ];
    }
    if (tab === 'oi') {
      const r = oiQ.data?.rows ?? [];
      const upUp = r.filter((x) => x.state === 'up_up').length;
      const downUp = r.filter((x) => x.state === 'down_up').length;
      const maxVol = pickTop(r, (x) => x.quoteVolume24h);
      return [
        { label: zh ? '价升·OI增' : 'Price↑ OI↑', value: `${upUp}`, tone: 'pos' as const },
        { label: zh ? '价跌·OI增' : 'Price↓ OI↑', value: `${downUp}`, tone: 'neg' as const },
        {
          label: (zh ? '成交额 Top' : 'Volume top') + cov(maxVol.valid, maxVol.total),
          value: maxVol.top ? maxVol.top.symbol : noCmp,
          tone: 'flat' as const,
        },
      ];
    }
    const r = crowding.data?.rows ?? [];
    // D18 组合规则：费率分位 与 OI 分位 均 ≥80 才算高拥挤
    const hot = r.filter((x) => (x.fundingPercentile ?? 0) >= 80 && (x.oiPercentile ?? 0) >= 80).length;
    const maxLiq = pickTop(r, (x) => x.liquidation24hUsd);
    return [
      { label: zh ? '高拥挤（费率与OI分位均≥80）' : 'Hot (rate & OI pct ≥ 80)', value: `${hot}`, tone: 'warn' as const },
      {
        label: (zh ? '24H 爆仓 Top' : 'Liq top') + cov(maxLiq.valid, maxLiq.total),
        value: maxLiq.top && maxLiq.top.liquidation24hUsd != null ? maxLiq.top.symbol : noCmp,
        tone: 'neg' as const,
      },
      { label: zh ? '标的数' : 'Symbols', value: `${r.length}`, tone: 'flat' as const },
    ];
  }, [tab, funding.data, oiQ.data, crowding.data, zh]);

  const sorted = useMemo(() => {
    // D09: 缺失（null）不参与排序胜出，恒排末尾。
    if (tab === 'funding' && sortKey === 'rate') return sortValidDesc(rows as FundingRow[], (x) => x.rate8h);
    if (tab === 'funding' && sortKey === 'oi') return sortValidDesc(rows as FundingRow[], (x) => x.openInterestUsd);
    if (tab === 'funding' && sortKey === 'vol') return sortValidDesc(rows as FundingRow[], (x) => x.quoteVolume24h);
    if (tab === 'oi' && sortKey === 'price') return sortValidDesc(rows as OiRow[], (x) => x.priceChangePct);
    if (tab === 'oi' && sortKey === 'oi') return sortValidDesc(rows as OiRow[], (x) => x.oiChangePct);
    if (tab === 'crowding' && sortKey === 'funding') return sortValidDesc(rows as CrowdingRow[], (x) => x.fundingPercentile);
    if (tab === 'crowding' && sortKey === 'oi') return sortValidDesc(rows as CrowdingRow[], (x) => x.oiPercentile);
    if (tab === 'crowding' && sortKey === 'liq') return sortValidDesc(rows as CrowdingRow[], (x) => x.liquidation24hUsd);
    return [...rows];
  }, [rows, sortKey, tab]);

  const th = (key: string, label: string, cls = '') => (
    <th className={`cursor-pointer select-none ${cls}`} onClick={() => setSortKey(key)}>
      {label}
      {sortKey === key && <span className="ml-0.5 text-[9px]">↓</span>}
    </th>
  );

  const statusLabel = (s: FundingRow['status']) =>
    s === 'published' ? (zh ? '当前公布' : 'Published') : s === 'settled' ? (zh ? '已结算' : 'Settled') : s === 'estimated' ? (zh ? '预估' : 'Estimated') : '—';

  return (
    <div className="mx-auto max-w-[1200px] px-3 py-4 sm:px-4">
      <ResearchTabs />
      <div className="mb-3 text-lg font-semibold">{zh ? '合约研究中心' : 'Derivatives research'}</div>

      {/* Tabs */}
      <div className="mb-3 flex w-full max-w-[320px] rounded-lg border border-gf-border bg-gf-surface2 p-0.5">
        {tabs.map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors sm:text-sm ${
              tab === t.id ? 'bg-gf-accent text-white' : 'text-gf-muted hover:text-gf-text'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Summary: mobile shows 2, desktop shows 3 */}
      <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-3">
        {summary.map((s, i) => (
          <div key={s.label} className={`card !p-3 ${i >= 2 ? 'hidden sm:block' : ''}`}>
            <div className="text-[11px] text-gf-muted">{s.label}</div>
            <div className={`mt-0.5 break-words text-sm font-semibold leading-snug sm:text-base ${s.tone === 'pos' ? 'text-gf-positive' : s.tone === 'neg' ? 'text-gf-negative' : s.tone === 'warn' ? 'text-gf-warn' : 'text-gf-text'}`}>
              {s.value}
            </div>
          </div>
        ))}
      </div>

      {/* Table */}
      <div className="card !p-0 overflow-hidden">
        {/* D13: 手机合约研究卡片 —— 币对/费率与周期/状态/历史分位，点开详情抽屉 */}
        <div className="flex flex-col gap-2 p-2 sm:hidden">
          {tab === 'funding' &&
            (sorted as FundingRow[]).map((x, i) => (
              <button
                key={x.symbol}
                type="button"
                onClick={() => setSelected(x.symbol)}
                className="block w-full rounded-md border border-gf-border bg-gf-surface p-3 text-left hover:border-gf-accent/50"
              >
                <div className="flex items-center gap-2">
                  <span className="w-5 text-xs text-gf-muted">{i + 1}</span>
                  <span className="font-semibold">{x.symbol}</span>
                  <span className="ml-auto font-mono text-sm">{x.price != null ? fmtPrice(x.price) : '—'}</span>
                  <Badge label={statusLabel(x.status)} tone={x.status === 'published' ? 'pos' : x.status === 'estimated' ? 'warn' : 'flat'} />
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <div className="text-gf-muted">{zh ? '费率 (8h)' : 'Rate (8h)'}</div>
                    <div className={`font-mono ${(x.rate8h ?? 0) >= 0 ? 'text-gf-positive' : 'text-gf-negative'}`}>
                      {x.rate8h == null ? '—' : `${(x.rate8h * 100).toFixed(4)}%`}
                    </div>
                  </div>
                  <div>
                    <div className="text-gf-muted">{zh ? '周期' : 'Period'}</div>
                    <div className="font-mono">{x.periodHours ? `${x.periodHours}h` : '—'}</div>
                  </div>
                  <div>
                    <div className="text-gf-muted">{zh ? '历史分位' : 'Percentile'}</div>
                    <div className={`font-mono ${(x.fundingPercentile ?? 0) >= 80 ? 'text-gf-warn' : 'text-gf-text'}`}>
                      {fmtPercentile(x.fundingPercentile, x.percentileSamples, zh)}
                    </div>
                  </div>
                  <div>
                    <div className="text-gf-muted">{zh ? '未平仓' : 'OI'}</div>
                    <div className="font-mono">{fmtMoney(x.openInterestUsd)}</div>
                  </div>
                </div>
              </button>
            ))}
          {tab === 'oi' &&
            (sorted as OiRow[]).map((x, i) => (
              <button
                key={x.symbol}
                type="button"
                onClick={() => setSelected(x.symbol)}
                className="block w-full rounded-md border border-gf-border bg-gf-surface p-3 text-left hover:border-gf-accent/50"
              >
                <div className="flex items-center gap-2">
                  <span className="w-5 text-xs text-gf-muted">{i + 1}</span>
                  <span className="font-semibold">{x.symbol}</span>
                  <span className="ml-auto font-mono text-sm">{x.price != null ? fmtPrice(x.price) : '—'}</span>
                  <Badge
                    label={
                      x.state === 'up_up'
                        ? zh ? '价升·OI增' : 'Price↑ OI↑'
                        : x.state === 'down_up'
                          ? zh ? '价跌·OI增' : 'Price↓ OI↑'
                          : x.state === 'up_down'
                            ? zh ? '价升·OI减' : 'Price↑ OI↓'
                            : x.state === 'down_down'
                              ? zh ? '价跌·OI减' : 'Price↓ OI↓'
                              : zh ? '平' : 'Flat'
                    }
                    tone={x.state === 'up_up' ? 'pos' : x.state === 'down_up' ? 'neg' : x.state === 'flat' ? 'flat' : 'warn'}
                  />
                </div>
                <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                  <div>
                    <div className="text-gf-muted">24H {zh ? '价格' : 'Price'}</div>
                    <div className={`font-mono ${(x.priceChangePct ?? 0) >= 0 ? 'text-gf-positive' : 'text-gf-negative'}`}>{fmtPct(x.priceChangePct)}</div>
                  </div>
                  <div>
                    <div className="text-gf-muted">24H OI</div>
                    <div className={`font-mono ${(x.oiChangePct ?? 0) >= 0 ? 'text-gf-positive' : 'text-gf-negative'}`}>{fmtPct(x.oiChangePct)}</div>
                  </div>
                  <div>
                    <div className="text-gf-muted">{zh ? '未平仓' : 'OI'}</div>
                    <div className="font-mono">{fmtMoney(x.openInterestUsd)}</div>
                  </div>
                </div>
              </button>
            ))}
          {tab === 'crowding' &&
            (sorted as CrowdingRow[]).map((x, i) => {
              // D18 拥挤度组合规则：费率分位 与 OI 分位 均 ≥80 才标「高拥挤」
              const hot = (x.fundingPercentile ?? 0) >= 80 && (x.oiPercentile ?? 0) >= 80;
              return (
                <button
                  key={x.symbol}
                  type="button"
                  onClick={() => setSelected(x.symbol)}
                  className="block w-full rounded-md border border-gf-border bg-gf-surface p-3 text-left hover:border-gf-accent/50"
                >
                  <div className="flex items-center gap-2">
                    <span className="w-5 text-xs text-gf-muted">{i + 1}</span>
                    <span className="font-semibold">{x.symbol}</span>
                    {hot && <span className="inline-block h-1.5 w-1.5 rounded-full bg-gf-warn" />}
                    <span className="ml-auto font-mono text-sm">{x.price != null ? fmtPrice(x.price) : '—'}</span>
                  </div>
                  <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
                    <div>
                      <div className="text-gf-muted">{zh ? '费率分位' : 'Rate pct'}</div>
                      <div className={`font-mono ${(x.fundingPercentile ?? 0) >= 80 ? 'text-gf-warn' : 'text-gf-text'}`}>
                        {fmtPercentile(x.fundingPercentile, x.fundingSamples, zh)}
                      </div>
                    </div>
                    <div>
                      <div className="text-gf-muted">OI {zh ? '分位' : 'pct'}</div>
                      <div className={`font-mono ${(x.oiPercentile ?? 0) >= 80 ? 'text-gf-warn' : 'text-gf-text'}`}>
                        {fmtPercentile(x.oiPercentile, x.oiSamples, zh)}
                      </div>
                    </div>
                    <div>
                      <div className="text-gf-muted">24H {zh ? '爆仓' : 'Liq'}</div>
                      <div className={`font-mono ${(x.liquidation24hUsd ?? 0) > 0 ? 'text-gf-negative' : 'text-gf-muted'}`}>{fmtMoney(x.liquidation24hUsd)}</div>
                    </div>
                  </div>
                </button>
              );
            })}
          {!loading && sorted.length === 0 && (
            <div className="py-8 text-center text-sm text-gf-muted">{zh ? '暂无有效数据' : 'No valid data'}</div>
          )}
        </div>
        <div className="overflow-x-auto">
          {tab === 'funding' && (
            <table className="table-base">
              <thead className="bg-gf-surface2/60">
                <tr>
                  <th>#</th>
                  <th className="sticky left-0 z-10 bg-gf-surface2/60">{zh ? '标的' : 'Symbol'}</th>
                  <th className="text-right">{zh ? '价格' : 'Price'}</th>
                  <th className="text-right hidden sm:table-cell">{zh ? '24H 成交额' : '24h Vol'}</th>
                  {th('rate', zh ? '费率 (8h)' : 'Rate (8h)', 'text-right')}
                  <th className="text-right hidden md:table-cell">{zh ? '周期' : 'Period'}</th>
                  <th className="text-center">{zh ? '状态' : 'Status'}</th>
                  <th className="text-right">{zh ? '历史分位' : 'Percentile'}</th>
                  <th className="text-right hidden lg:table-cell">{zh ? '未平仓' : 'OI'}</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r, i) => {
                  const x = r as FundingRow;
                  return (
                    <tr key={x.symbol} className="cursor-pointer hover:bg-gf-surface2/40" onClick={() => setSelected(x.symbol)}>
                      <td className="text-xs text-gf-muted">{i + 1}</td>
                      <td className="sticky left-0 z-10 bg-gf-surface font-medium">{x.symbol}</td>
                      <td className="text-right font-mono text-xs">{fmtPrice(x.price)}</td>
                      <td className="text-right text-xs text-gf-muted hidden sm:table-cell">{formatVolume(x.quoteVolume24h)}</td>
                      <td className={`text-right font-mono text-xs ${(x.rate8h ?? 0) >= 0 ? 'text-gf-positive' : 'text-gf-negative'}`}>
                        {x.rate8h == null ? '—' : `${(x.rate8h * 100).toFixed(4)}%`}
                      </td>
                      <td className="text-right text-xs text-gf-muted hidden md:table-cell">{x.periodHours ? `${x.periodHours}h` : '—'}</td>
                      <td className="text-center">
                        <Badge label={statusLabel(x.status)} tone={x.status === 'published' ? 'pos' : x.status === 'estimated' ? 'warn' : 'flat'} />
                      </td>
                      <td className={`text-right font-mono text-xs ${(x.fundingPercentile ?? 0) >= 80 ? 'text-gf-warn' : 'text-gf-text'}`}>
                        {fmtPercentile(x.fundingPercentile, x.percentileSamples, zh)}
                      </td>
                      <td className="text-right text-xs text-gf-muted hidden lg:table-cell">{fmtMoney(x.openInterestUsd)}</td>
                    </tr>
                  );
                })}
                {!loading && sorted.length === 0 && (
                  <tr>
                    <td colSpan={9} className="py-8 text-center text-gf-muted">{zh ? '暂无有效数据' : 'No valid data'}</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {tab === 'oi' && (
            <table className="table-base">
              <thead className="bg-gf-surface2/60">
                <tr>
                  <th>#</th>
                  <th className="sticky left-0 z-10 bg-gf-surface2/60">{zh ? '标的' : 'Symbol'}</th>
                  <th className="text-right hidden sm:table-cell">{zh ? '价格' : 'Price'}</th>
                  {th('price', zh ? '24H 价格' : '24h price', 'text-right')}
                  {th('oi', zh ? '24H OI' : '24h OI', 'text-right')}
                  <th className="text-right hidden md:table-cell">{zh ? '口径' : 'Basis'}</th>
                  <th className="text-center">{zh ? '状态' : 'State'}</th>
                  <th className="text-right hidden lg:table-cell">{zh ? '未平仓' : 'OI'}</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r, i) => {
                  const x = r as OiRow;
                  const stateLabel =
                    x.state === 'up_up'
                      ? zh ? '价升·OI增' : 'Price↑ OI↑'
                      : x.state === 'down_up'
                        ? zh ? '价跌·OI增' : 'Price↓ OI↑'
                        : x.state === 'up_down'
                          ? zh ? '价升·OI减' : 'Price↑ OI↓'
                          : x.state === 'down_down'
                            ? zh ? '价跌·OI减' : 'Price↓ OI↓'
                            : zh ? '平' : 'Flat';
                  return (
                    <tr key={x.symbol} className="cursor-pointer hover:bg-gf-surface2/40" onClick={() => setSelected(x.symbol)}>
                      <td className="text-xs text-gf-muted">{i + 1}</td>
                      <td className="sticky left-0 z-10 bg-gf-surface font-medium">{x.symbol}</td>
                      <td className="text-right font-mono text-xs hidden sm:table-cell">{fmtPrice(x.price)}</td>
                      <td className={`text-right font-mono text-xs ${(x.priceChangePct ?? 0) >= 0 ? 'text-gf-positive' : 'text-gf-negative'}`}>{fmtPct(x.priceChangePct)}</td>
                      <td className={`text-right font-mono text-xs ${(x.oiChangePct ?? 0) >= 0 ? 'text-gf-positive' : 'text-gf-negative'}`}>{fmtPct(x.oiChangePct)}</td>
                      <td className="text-right text-[10px] text-gf-muted hidden md:table-cell">{x.oiBasis === 'usd' ? (zh ? '金额' : 'USD') : (zh ? '数量' : 'Qty')}</td>
                      <td className="text-center">
                        <Badge
                          label={stateLabel}
                          tone={x.state === 'up_up' ? 'pos' : x.state === 'down_up' ? 'neg' : x.state === 'flat' ? 'flat' : 'warn'}
                        />
                      </td>
                      <td className="text-right text-xs text-gf-muted hidden lg:table-cell">{fmtMoney(x.openInterestUsd)}</td>
                    </tr>
                  );
                })}
                {!loading && sorted.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-gf-muted">{zh ? '暂无有效数据' : 'No valid data'}</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}

          {tab === 'crowding' && (
            <table className="table-base">
              <thead className="bg-gf-surface2/60">
                <tr>
                  <th>#</th>
                  <th className="sticky left-0 z-10 bg-gf-surface2/60">{zh ? '标的' : 'Symbol'}</th>
                  <th className="text-right hidden sm:table-cell">{zh ? '价格' : 'Price'}</th>
                  {th('funding', zh ? '费率分位' : 'Rate pct', 'text-right')}
                  {th('oi', zh ? 'OI 分位' : 'OI pct', 'text-right')}
                  <th className="text-right hidden md:table-cell">{zh ? '多空比' : 'L/S'}</th>
                  {th('liq', zh ? '24H 爆仓' : '24h liq', 'text-right')}
                  <th className="text-right hidden lg:table-cell">{zh ? '溢价' : 'Premium'}</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((r, i) => {
                  const x = r as CrowdingRow;
                  // D18 拥挤度组合规则：费率分位 与 OI 分位 均 ≥80 才标「高拥挤」
                  const hot = (x.fundingPercentile ?? 0) >= 80 && (x.oiPercentile ?? 0) >= 80;
                  return (
                    <tr key={x.symbol} className="cursor-pointer hover:bg-gf-surface2/40" onClick={() => setSelected(x.symbol)}>
                      <td className="text-xs text-gf-muted">{i + 1}</td>
                      <td className="sticky left-0 z-10 bg-gf-surface font-medium">
                        {x.symbol}
                        {hot && <span className="ml-1 inline-block h-1.5 w-1.5 rounded-full bg-gf-warn" />}
                      </td>
                      <td className="text-right font-mono text-xs hidden sm:table-cell">{fmtPrice(x.price)}</td>
                      <td className={`text-right font-mono text-xs ${(x.fundingPercentile ?? 0) >= 80 ? 'text-gf-warn' : 'text-gf-text'}`}>
                        {fmtPercentile(x.fundingPercentile, x.fundingSamples, zh)}
                      </td>
                      <td className={`text-right font-mono text-xs ${(x.oiPercentile ?? 0) >= 80 ? 'text-gf-warn' : 'text-gf-text'}`}>
                        {fmtPercentile(x.oiPercentile, x.oiSamples, zh)}
                      </td>
                      <td className="text-right font-mono text-xs text-gf-muted hidden md:table-cell">{x.longShortRatio != null ? x.longShortRatio.toFixed(2) + 'x' : '—'}</td>
                      <td className={`text-right font-mono text-xs ${(x.liquidation24hUsd ?? 0) > 0 ? 'text-gf-negative' : 'text-gf-muted'}`}>{fmtMoney(x.liquidation24hUsd)}</td>
                      <td className={`text-right font-mono text-xs hidden lg:table-cell ${(x.premiumPct ?? 0) >= 0 ? 'text-gf-positive' : 'text-gf-negative'}`}>{fmtPct(x.premiumPct)}</td>
                    </tr>
                  );
                })}
                {!loading && sorted.length === 0 && (
                  <tr>
                    <td colSpan={8} className="py-8 text-center text-gf-muted">{zh ? '暂无有效数据' : 'No valid data'}</td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        {basis && <p className="border-t border-gf-border/60 px-3 py-2 text-[10px] text-gf-muted">{basis}</p>}
      </div>

      {selected && <DetailDrawer symbol={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

export default function DerivativesPage() {
  const authReady = useRequireAuth('/research/derivatives', true);
    return (
    <Suspense fallback={null}>
      <EventPing event="research_start" />
      <DerivativesBody />
    </Suspense>
  );
}
